import {
    Component,
    Injectable,
    OnInit,
    inject
} from '@angular/core';

import {
    KeyValue
} from '@angular/common';

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef
} from "@angular/material/dialog";

import {
    lastValueFrom
} from 'rxjs';

import {
    BaseDialogService, NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    CustomForm,
    CustomFormControl,
    CustomFormControlProps,
    CustomFormGroup,
    InputType,
    KeyValueIconImage,
    TilbyMagicFormComponent
} from '@tilby/tilby-ui-lib/components/tilby-magic-form';

import {
    ConfigurationManagerService,
    EntityManagerService
} from 'src/app/core';

import {
    Validators
} from '@angular/forms';

import {
    Bookings,
    Channels,
    Customers,
    Rooms,
    RoomsTables,
    Sales,
    SalesCustomer
} from 'tilby-models';

import {
    TranslateService
} from '@ngx-translate/core';

import {
    TilbyDatePipe
} from '@tilby/tilby-ui-lib/pipes/tilby-date';

type SaleFormFields = {
    type: string,
    name: string,
    delivery_at: string,
    channels: string,
    order_id: string,
    table_id: string,
    covers: number,
    booking_id: string,
    notes: string,
}

export type AddEditSaleDialogData = {
    formParams: AddEditSaleDialogFormParams,
    isEdit: boolean,
    tables: (RoomsTables & { room: Rooms })[]
}

export type AddEditSaleDialogFormParams = {
    id?: Sales['id'],
    name?: Sales['name'],
    saleType: Sales['order_type'] | Sales['external_id'],
    table?: {
        id: Sales['table_id'],
        covers: Sales['covers']
    },
    room?: Rooms,
    booking?: Partial<Bookings>,
    order_id?: Sales['external_id'],
    delivery_at?: Sales['deliver_at'],
    channel?: Sales['channel'],
    customer?: Customers | SalesCustomer,
    longPress?: boolean,
    notes?: string,
}

@Component({
    selector: 'app-add-edit-sale-dialog',
    standalone: true,
    imports: [
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        MatDialogModule,
        TilbyMagicFormComponent,
    ],
    templateUrl: './add-edit-sale-dialog.component.html',
    styleUrls: ['./add-edit-sale-dialog.component.scss']
})
export class AddEditSaleDialogComponent implements OnInit {
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly translateService = inject(TranslateService);

    private readonly data: AddEditSaleDialogData = inject(MAT_DIALOG_DATA);
    private readonly matDialogRef = inject(MatDialogRef);

    protected readonly title = `TABLES_NEW.DIALOGS.SALE.TITLE${this.data.isEdit ? '_MODIFY' : ''}`
    protected saleForm?: CustomFormGroup<CustomForm<SaleFormFields>>;

    async ngOnInit() {
        const deliveryChannels = await this.entityManagerService.channels.fetchCollectionOffline().then((channels) => channels.filter((channel) => channel.id !== 'pos'));

        this.saleForm = this.createForm(this.data.formParams, deliveryChannels, this.data.tables, this.data.isEdit);
    }

    public createForm(params: AddEditSaleDialogFormParams, deliveryChannels: Channels[], tables: (RoomsTables & { room: Rooms })[], isEdit: boolean) {
        const deliveryEnabled = this.configurationManagerService.isModuleEnabled('orders');
        const tablesEnabled = this.configurationManagerService.isModuleEnabled('tables');

        const types = [
            { key: 'ORDERS.NEW_ORDER.NORMAL', value: 'normal' },
            ...(deliveryEnabled || this.data.formParams.saleType === 'take_away' ? [{ key: 'ORDERS.NEW_ORDER.TAKE_AWAY', value: 'take_away' }] : []),
            ...(deliveryEnabled || this.data.formParams.saleType === 'delivery' ? [{ key: 'ORDERS.NEW_ORDER.DELIVERY', value: 'delivery' }] : [])
        ];

        const channels = deliveryChannels.map((item: { id: any; name: any; }) => {
            return {
                key: item.name,
                value: item.id,
                image: `assets/images/channels/${item.id}.png`,
                defaultImage: `assets/images/channels/pos.png`,
            };
        });

        const typeChoices: KeyValue<string, string>[] = Array.from(types);
        const channelChoices: KeyValueIconImage<string>[] = Array.from(channels);
        const tableChoices = tables.map((item: any) => {
            return { key: item.room.name + ' - ' + item.name, value: item.id };
        });
        const coversActions = {
            prefix: { icon: 'remove', callback: () => this.covers?.setValue(Math.max(0, (this.covers.value || 0) - 1)) },
            suffix: { icon: 'add', callback: () => this.covers?.setValue((this.covers.value || 0) + 1) },
        };

        const linkedTableId = params.table?.id;
        const coversFilled = params.booking?.people || params.table?.covers;
        const showCovers = linkedTableId || coversFilled;

        const controlTypes: Record<string, InputType> = {
            type: 'select',
            name: 'text',
            delivery_at: 'datetime',
            channels: 'select',
            order_id: 'text',
            table_id: 'select',
            covers: 'number',
            booking_id: 'text',
            notes: 'textarea',
        };

        const saleForm = new CustomFormGroup<CustomForm<SaleFormFields>>({
            type: new CustomFormControl(params.saleType || 'normal', {}, {
                ...new CustomFormControlProps(),
                label: 'TABLES_NEW.DIALOGS.SALE.TYPE',
                inputType: controlTypes['type'],
                class: 'tw-w-full',
                inputChoices: typeChoices
            }),
            name: new CustomFormControl(params.name || `${this.translateService.instant('TABLES_NEW.DIALOGS.SALE.NAME_TEMPLATE')}`, { validators: [Validators.required, Validators.maxLength(30)] }, {
                ...new CustomFormControlProps(),
                label: 'TABLES_NEW.DIALOGS.SALE.NAME',
                class: 'tw-w-full',
                inputType: controlTypes['name'],
                hint: () => `${this.name?.value?.length || 0}/30`,
                customActions: { suffix: { icon: 'cancel', callback: () => this.name?.reset() } }
            }),
            delivery_at: new CustomFormControl(params.delivery_at || TilbyDatePipe.date(), {}, {
                ...new CustomFormControlProps(),
                label: 'TABLES_NEW.DIALOGS.SALE.DELIVERY_AT',
                inputType: controlTypes['delivery_at'],
                class: 'tw-w-full'
            }),
            channels: new CustomFormControl(params.channel || '', {}, {
                ...new CustomFormControlProps(),
                label: 'TABLES_NEW.DIALOGS.SALE.CHANNEL',
                inputType: controlTypes['channels'],
                class: 'tw-w-full',
                inputChoices: channelChoices
            }),
            order_id: new CustomFormControl(params.order_id || '', {}, {
                ...new CustomFormControlProps(),
                label: 'TABLES_NEW.DIALOGS.SALE.ORDER_ID',
                inputType: controlTypes['order_id'],
                class: 'tw-w-full'
            }),
            table_id: new CustomFormControl(linkedTableId || '', {}, {
                ...new CustomFormControlProps(),
                label: 'TABLES_NEW.DIALOGS.SALE.TABLE',
                inputType: controlTypes['table_id'],
                class: 'tw-w-full',
                inputChoices: tableChoices,
                customActions: { suffix: { icon: 'cancel', callback: () => { this.table_id?.reset() } } }
            }),
            covers: new CustomFormControl(Math.trunc(coversFilled || 0), { validators: [Validators.required, Validators.pattern('^[0-9]{1,4}$'), Validators.maxLength(4)] }, {
                ...new CustomFormControlProps(),
                label: 'TABLES_NEW.DIALOGS.SALE.COVERS',
                inputType: showCovers ? controlTypes['covers'] : 'hidden',
                customActions: coversActions,
                class: 'tw-w-full'
            }),
            booking_id: new CustomFormControl(params.booking?.id || '', {}, {
                ...new CustomFormControlProps(),
                label: '',
                inputType: controlTypes['booking_id'],
                class: 'tw-w-full'
            }),
            notes: new CustomFormControl(params.notes || '', {}, {
                ...new CustomFormControlProps(),
                inputType: isEdit ? controlTypes['notes'] : 'hidden',
                label: 'TABLES_NEW.DIALOGS.SALE.NOTES',
                class: 'tw-w-full'
            }),
        });

        const changesFunction = (value: string) => {
            //Disable fields
            const fields: (keyof SaleFormFields)[] = ['booking_id', 'channels', 'covers', 'delivery_at', 'order_id', 'table_id'];
            const fieldsToEnable = new Set<keyof SaleFormFields>();

            switch (value) {
                case 'normal':
                    if(tablesEnabled) {
                        fieldsToEnable.add('table_id');
                        fieldsToEnable.add('covers');
                    }
                    break;
                case 'take_away':
                    fieldsToEnable.add('delivery_at');
                    break;
                case 'delivery':
                    fieldsToEnable.add('delivery_at');

                    if(deliveryEnabled) {
                        fieldsToEnable.add('channels');
                        fieldsToEnable.add('order_id');
                    }
                    break;
            }

            for(const field of fields) {
                saleForm.controls[field].customProps.inputType = fieldsToEnable.has(field) ? controlTypes[field] : 'hidden';
            }
        }

        saleForm.controls.type.valueChanges.subscribe((value) => {
            changesFunction(value);
        });

        saleForm.controls.table_id.valueChanges.subscribe((tableId) => {
            this.covers.customProps.inputType = (tablesEnabled && (tableId || this.covers.value)) ? 'number' : 'hidden';
        });

        changesFunction(params.saleType || 'normal');

        return saleForm;
    }

    private get name() {
        return this.saleForm?.controls.name;
    }

    private get table_id() {
        return this.saleForm?.controls.table_id!;
    }

    private get covers() {
        return this.saleForm?.controls.covers!;
    }

    confirm(): void {
        this.matDialogRef.close(this.saleForm?.value);
    }
}

@Injectable({
    providedIn: 'root'
})
export class AddEditSaleDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(data: AddEditSaleDialogData): Promise<SaleFormFields> {
        const config: NonNullableConfigData<AddEditSaleDialogData> = {
            ...this.switchMobileDesktopDimensions({
                maxWidth: '800px'
            }),
            disableClose: true,
            data: data
        };

        return lastValueFrom(this.dialogRef.open(AddEditSaleDialogComponent, config).afterClosed());
    }
}
